import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Separator } from './Separator';

import { cn } from '@/lib/helper/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
   React.ElementRef<typeof DialogPrimitive.Overlay>,
   React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
   <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
         'fixed inset-0 z-50 bg-black/45 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
         className
      )}
      {...props}
   />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentWithOverlaySettingProps
   extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
   overlay?: boolean;
   enableClose?: boolean;
}

const DialogContent = React.forwardRef<
   React.ElementRef<typeof DialogPrimitive.Content>,
   DialogContentWithOverlaySettingProps
>(
   (
      { className, children, overlay = false, enableClose = true, ...props },
      ref
   ) => (
      <DialogPortal>
         <DialogOverlay
            className={overlay ? 'opacity-50 dark:opacity-80' : 'opacity-0'}
         />
         <DialogPrimitive.Content
            ref={ref}
            className={cn(
               'fixed left-[50%] top-[50%] z-50 grid w-full max-w-md translate-x-[-50%] translate-y-[-50%] border-tertiary bg-secondary shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-2xl overflow-hidden transition-none',
               className
            )}
            {...props}
         >
            {children}
            <DialogPrimitive.Close
               className={cn(
                  'absolute stroke-2 right-2 top-2 rounded-sm opacity-100 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none',
                  !enableClose && 'hidden'
               )}
            >
               <Cross2Icon className="h-4 w-4" />
               <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
         </DialogPrimitive.Content>
      </DialogPortal>
   )
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
   className,
   ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
   <div
      className={cn(
         'flex flex-col space-y-1.5 text-left sm:text-left bg-tertiary p-4',
         className
      )}
      {...props}
   />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
   className,
   children,
   ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
   <div className={cn('flex flex-col-reverse', className)} {...props}>
      {children}
      <Separator />
   </div>
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
   React.ElementRef<typeof DialogPrimitive.Title>,
   React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
   <DialogPrimitive.Title
      ref={ref}
      className={cn('text-lg font-medium leading-none', className)}
      {...props}
   />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
   React.ElementRef<typeof DialogPrimitive.Description>,
   React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
   <DialogPrimitive.Description
      ref={ref}
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
   />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
   Dialog,
   DialogPortal,
   DialogOverlay,
   DialogTrigger,
   DialogClose,
   DialogContent,
   DialogHeader,
   DialogFooter,
   DialogTitle,
   DialogDescription,
};
